import React, { useMemo, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Icon,
  Tooltip
} from "@mui/material";
import "./style.css";
import { useQueryClient } from "@tanstack/react-query";
import { useDeleteAssistantFile, useDownloadAssistantFile } from "queries/chat";

const KnowledgeBaseFiles = ({
  tabsData = [],
  waiting,
  setWaiting,
  loadAssistant,
  assistantNames = [],
  setKnowledgebaseData,
  pagination,
  onLoad
}) => {
  const queryClient = useQueryClient();
  const downloadAssistantFile = useDownloadAssistantFile();

  const [searchQuery, setSearchQuery] = useState("");
  const [selectedItems, setSelectedItems] = useState([]);
  const [openConfirmDialog, setOpenConfirmDialog] = useState(false);
  const [openConfirmDialog2, setOpenConfirmDialog2] = useState(false);
  const [loadingItem, setLoadingItem] = useState(null); // Track which file is being deleted
  const [fileToDelete, setFileToDelete] = useState(null);
  const [assistantForDelete, setAssistantForDelete] = useState(null);
  const [selectedAssistant, setSelectedAssistant] = useState("All");
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  // Combine all assistant_files into a single array for easier handling
  const assistants = useMemo(() => ([
    "All",
    ...assistantNames,
  ]), [assistantNames])

  var filteredFiles = useMemo(() => (
    tabsData?.filter((file) => {
      const matchesAssistant =
        selectedAssistant === "All" || file?.assistant_name === selectedAssistant;

      const assistantName = file?.assistant_name?.toLowerCase() || "";
      const fileName = file?.name?.toLowerCase() || "";
      const uploadedBy = file?.username?.toLowerCase() || "";

      const matchesSearch =
        assistantName.includes(searchQuery.toLowerCase()) ||
        fileName.includes(searchQuery.toLowerCase()) ||
        uploadedBy.includes(searchQuery.toLowerCase());

      return matchesAssistant && matchesSearch;
    })
  ), [selectedAssistant, tabsData]);


  // Pagination logic
  if (filteredFiles?.length > 0) {
    var totalPages = pagination.total
    var currentItems = filteredFiles
  }

  const handleCheckboxChange = (file) => {
    setSelectedItems(prev => {
      const findFile = prev.find(item => item.id === file.id)
      if (findFile)
        return prev.filter((item) => item.id !== file.id)
      else return [...prev, file]
    })
  };

  const handlePageChange = (pageNumber) => {
    if (pageNumber === page) return;
    onLoad(pageNumber);
    setCurrentPage(pageNumber);
  };

  const { mutateAsync: deleteFile } = useDeleteAssistantFile();
  const invalidateKnowledgeBase = () =>
    queryClient.invalidateQueries({ queryKey: ["knowledge-base"] });

  const deleteAllFiles = async () => {
    if (selectedItems.length === 0) return; // Ensure there are files selected to delete

    try {
      console.log("deleting----"); // Show loading indicator
      for (const fileId of selectedItems) {
        const response = await deleteFile(
          { fileId: fileId.id, assistantId: fileId?.assistant_id },
          { onSuccess: invalidateKnowledgeBase },
        );

        if (response) {
          setSelectedItems((prev) => prev.filter((item) => item.id !== fileId.id)); // Remove deleted file ID from selected
        }
      }

      setOpenConfirmDialog2(false);
    } catch (error) {
      console.error("Error deleting files:", error);
    }
  };

  const handleDeleteFile = async () => {

    if (!fileToDelete || !fileToDelete?.assistant_id) return;

    setLoadingItem(fileToDelete?.id); // Set the loading state for the specific item
    try {
      const response = await deleteFile(
        { fileId: fileToDelete?.id, assistantId: fileToDelete?.assistant_id },
        { onSuccess: invalidateKnowledgeBase },
      );
      if (response) {
        setLoadingItem(null);
      }
    } catch (error) {
      console.error("Error deleting file:", error);
    } finally {
      setOpenConfirmDialog(false); // Close the dialog after handling the deletion
    }
  };

  const confirmDeleteFile = (fileItem) => {
    const assistant = tabsData.find((tab) =>
      tab?.assistant_files?.some((file) => file?.id === fileItem?.id)
    );
    setFileToDelete(fileItem);
    setAssistantForDelete(assistant);
    setOpenConfirmDialog(true); // Open the confirmation dialog
  };

  const handleDownloadFile = async (fileItem) => {
    try {
      // Call the file download API
      const response = await downloadAssistantFile(fileItem?.id, { toaster: true });

      console.log(response, "response1233");

      if (response?.response?.data?.download_url) {
        // Fetch the file content using fetch API

        const fileUrl = response?.response?.data?.download_url;
        const fetchResponse = await fetch(fileUrl);
        const blob = await fetchResponse.blob();

        // Create object URL from blob
        const objectUrl = window.URL.createObjectURL(blob);

        // Create an anchor element for the download
        const a = document.createElement("a");
        a.href = objectUrl;
        // Use original filename if available, otherwise fallback to ID
        const fileName = fileItem?.name || `${fileItem?.id}.pdf`;
        a.download = fileName;

        // Hide the anchor element
        a.style.display = "none";

        // Append to body, click, and cleanup
        document.body.appendChild(a);
        a.click();

        // Cleanup
        window.URL.revokeObjectURL(objectUrl);
        document.body.removeChild(a);
      } else {
        console.error("Download URL not found in response.");
      }
    } catch (error) {
      console.error("Error downloading file:", error);
      // You might want to show an error message to the user here
    }
  };

  const maxButtonsToShow = 5;
  const startPage = useMemo(
    () => Math.max(currentPage - Math.floor(maxButtonsToShow / 2), 1),
    [currentPage]
  );
  const endPage = useMemo(
    () => Math.min(startPage + maxButtonsToShow - 1, totalPages),
    [startPage, totalPages]
  );

  return (
    <Box sx={{ width: "100%", typography: "body1" }}>
      {/* Search Field */}
      <div style={{ marginBottom: "16px", display: "flex", justifyContent: "space-between" }}>
        <button
          style={{
            background: selectedItems.length > 0 ? "#e31f1f" : "#666666",
            color: "white",
            borderRadius: "10px",
            padding: "10px 20px",
            border: "none",
            cursor: selectedItems.length > 0 ? "pointer" : "not-allowed",
          }}
          onClick={() => setOpenConfirmDialog2(true)}
          disabled={selectedItems.length === 0} // Disable button if no items are selected
        >
          Delete All
        </button>

        <div>
          <input
            type="text"
            placeholder="Search files..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            style={{
              padding: "8px",
              border: "1px solid #ccc",
              borderRadius: "4px",
              width: "300px",
            }}
          />

          <select
            id="assistant-selector"
            value={selectedAssistant}
            onChange={(e) => setSelectedAssistant(e.target.value)}
            style={{
              padding: "11px 8px",
              border: "1px solid #ccc",
              borderRadius: "4px",
              width: "160px",
              marginLeft: "10px",
            }}
          >
            {assistants?.map((assistant) => (
              <option key={assistant} value={assistant}>
                {assistant === "Default-6683d9932ad0d58aead153d0" ? "Custom Task" : assistant}
              </option>
            ))}
          </select>
        </div>
      </div>

      <table style={{ width: "100%" }}>
        {/* Table Header */}
        <div className="table_heading">
          <p className="td_border" style={{ padding: "21px", width: "10%" }}></p>

          <p className="td_border" style={{ padding: "21px", width: "22.5%" }}>
            Assistant
          </p>
          <p className="td_border" style={{ padding: "21px", width: "22.5%" }}>
            File Name
          </p>
          <p className="td_border" style={{ padding: "21px", width: "22.5%" }}>
            Date Created
          </p>

          <p className="td_border" style={{ padding: "21px", width: "22.5%" }}></p>
        </div>

        {waiting ? (
          <p align="center">Loading Files...</p>
        ) : !currentItems || currentItems?.length === 0 ? <p align="center">No Files Found</p> : (
          <>
            {/* Table Body */}
            {currentItems?.map((file, fileIndex) => (
              <div
                key={file?.id || fileIndex}
                className="table_body"
                style={{ background: "#FFFFFF", alignItems: "center" }}
              >
                <p
                  className="td_border"
                  style={{
                    padding: "21px",
                    margin: "2px",
                    width: "10%",
                    alignItems: "center",
                    display: "flex",
                  }}
                >
                  <input
                    type="checkbox"
                    checked={selectedItems.find(item => item.id === file.id) ? true : false}
                    onChange={() => handleCheckboxChange(file)}
                  />
                </p>
                <p className="td_border" style={{ padding: "21px", width: "22.5%" }}>
                  {file?.assistant_name === "Default-6683d9932ad0d58aead153d0"
                    ? "Custom Task"
                    : file?.assistant_name}
                </p>
                <Tooltip title={file.name} placement="top">
                  <p className="td_border" style={{ padding: "21px", width: "22.5%" }}>
                    {file?.name?.length > 15 ? file?.name?.slice(0, 15) + "..." : file?.name}
                  </p>
                </Tooltip>
                <p className="td_border" style={{ padding: "21px", width: "22.5%" }}>
                  {new Date(file?.created_at).toLocaleDateString()}
                </p>

                <p
                  className="td_border"
                  style={{
                    padding: "21px",
                    width: "22.5%",
                    paddingRight: "35px",
                    textAlign: "right",
                    display: "flex",
                    justifyContent: "right",
                  }}
                >
                  <div
                    onClick={() => handleDownloadFile(file)}
                    style={{ display: "flex", alignItems: "center", cursor: "pointer" }}
                  >
                    <Icon>download</Icon>
                  </div>

                  <button
                    className="position-relative"
                    type="button"
                    style={{
                      marginLeft: "20px",
                      background: "#e31f1f",
                      color: "#fff",
                      padding: "6px 12px",
                      border: "none",
                      borderRadius: "4px",
                      cursor: "pointer",
                    }}
                    disabled={loadingItem === file?.id}
                    onClick={() => confirmDeleteFile(file)} // Open confirmation dialog on delete
                  >
                    {loadingItem === file?.id ? (
                      <div
                        className="spinner"
                        style={{
                          width: "16px",
                          height: "16px",
                          border: "2px solid #fff",
                          borderTop: "2px solid transparent",
                          borderRadius: "50%",
                          animation: "spin 0.75s linear infinite",
                          marginRight: "8px",
                        }}
                      ></div>
                    ) : (
                      <Icon>delete</Icon>
                    )}
                  </button>
                </p>
              </div>
            ))}
          </>
        )}
      </table>

      {/* Pagination Controls */}
      <div style={{ display: "flex", justifyContent: "center", margin: "16px 0px" }}>
        <button
          disabled={currentPage === 1}
          onClick={() => handlePageChange(currentPage - 1)}
          style={{
            padding: "8px 16px",
            margin: "0 5px",
            backgroundColor: "white",
            color: "black",
            border: "1px solid black",
            borderRadius: "4px",
            cursor: currentPage === 1 ? "not-allowed" : "pointer",
            opacity: currentPage === 1 ? 0.6 : 1,
          }}
        >
          Previous
        </button>
        {endPage && startPage && [...Array(endPage - startPage + 1)]?.map((_, index) => {
          const pageNumber = startPage + index;
          return (
            <button
              key={index}
              onClick={() => handlePageChange(pageNumber)}
              style={{
                padding: "8px 16px",
                margin: "0 5px",
                backgroundColor: currentPage === pageNumber ? "black" : "#FFFFFF",
                color: currentPage === pageNumber ? "#FFFFFF" : "#000000",
                border: currentPage === pageNumber ? "1px solid white" : "1px solid black",
                borderRadius: "4px",
                cursor: "pointer",
              }}
            >
              {pageNumber}
            </button>
          );
        })}
        <button
          disabled={currentPage === totalPages}
          onClick={() => handlePageChange(currentPage + 1)}
          style={{
            padding: "8px 16px",
            margin: "0 5px",
            backgroundColor: "white",
            color: "black",
            border: "1px solid black",
            borderRadius: "4px",
            cursor: currentPage === totalPages ? "not-allowed" : "pointer",
            opacity: currentPage === totalPages ? 0.6 : 1,
          }}
        >
          Next
        </button>
      </div>

      <Dialog open={openConfirmDialog} onClose={() => setOpenConfirmDialog(false)}>
        <DialogTitle>Delete Confirmation</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this file? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenConfirmDialog(false)} color="secondary">
            Cancel
          </Button>
          <Button onClick={handleDeleteFile} style={{ color: "#e31f1f" }} autoFocus>
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={openConfirmDialog2} onClose={() => setOpenConfirmDialog2(false)}>
        <DialogTitle>Delete Confirmation</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete these files? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenConfirmDialog2(false)} color="secondary">
            Cancel
          </Button>
          <Button onClick={deleteAllFiles} style={{ color: "#e31f1f" }} autoFocus>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default KnowledgeBaseFiles;
